import { Directive, ElementRef, Input, OnChanges, OnInit, Renderer2 } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedChanges, startWithFactory } from '@platform-frontend/util';
import { EMPTY, Observable, Subject, combineLatest, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, takeWhile, tap } from 'rxjs/operators';
import { IconRegistryService } from '../icons';
import { ActionFilter } from './action-filter/action-filter';
import { LoadingIndicatorService } from './loading-indicator.service';
import { SpinnerDirective } from './spinner.directive';

@UntilDestroy()
@Directive({
  selector: '[yatAutoSpinner]'
})
export class AutoSpinnerDirective extends SpinnerDirective implements OnInit, OnChanges {
  constructor(private loadingIndicatorService: LoadingIndicatorService, element: ElementRef, iconRegistryService: IconRegistryService, renderer: Renderer2) {
    super(element, iconRegistryService, renderer);
  }

  @Input()
  asyncForm?: FormGroup;

  @Input('yatAutoSpinner')
  actions?: ActionFilter | null | '';

  private actionFilters$: Subject<ActionFilter | null> = new Subject();

  ngOnInit(): void {
    const actionInProgress$ = this.actionFilters$.pipe(
      startWith(this.actions || null),
      switchMap(filter => this.loadingIndicatorService.getIsPending(filter))
    );

    // Debounce the status because
    // * emitting `true` would immediately deactivate the submit button and suppress
    //   the forms submit event in Chrome
    // * emitting `false` followed by an immediate `true` would unnecessarily reset
    //   the spinner between validation and actionInProgress
    combineLatest([actionInProgress$, this.isValidatingAfterClick()])
      .pipe(
        map(([actionInProgress, isValidatingAfterClick]) => actionInProgress || isValidatingAfterClick),
        debounceTime(0),
        distinctUntilChanged(),
        tap(spinning => {
          this.spinning = spinning;
          super.updateSpinner();
          super.updateHideContent();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngOnChanges(changes: TypedChanges<AutoSpinnerDirective>): void {
    if (changes.actions) {
      this.actionFilters$.next(this.actions || null);
    }

    if (changes.hideContentWhileSpinning) {
      super.updateHideContent();
    }
  }

  private isValidatingAfterClick(): Observable<boolean> {
    const formGroup = this.asyncForm;

    const isFormGroupPending$ =
      formGroup?.statusChanges?.pipe(
        startWithFactory(() => formGroup.status),
        map(status => status === 'PENDING'),
        takeWhile(validating => validating, true)
      ) ?? EMPTY;

    return fromEvent(this.element?.nativeElement, 'click').pipe(
      switchMap(() => isFormGroupPending$),
      startWith(false),
      distinctUntilChanged()
    );
  }
}
